import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent, SlideshowSelectEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest(".shopify-section, dialog");

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect, { signal });

    if (this.dataset.mediaTypeFilter) {
      const applyFilter = () => {
        if (!this.dataset.mediaTypeFilter) return;

        this.filterMediaType(this.dataset.mediaTypeFilter);
      };

      // Slideshow finishes its own setup in scheduler + rAF; wait for slides to exist.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (this.slideshow?.refs?.slides?.length) {
            applyFilter();
          } else {
            setTimeout(applyFilter, 50);
          }
        });
      });
    }
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Pauses playing media when the slideshow changes slides.
   * Keeps the newly selected slide's media untouched so click-to-pause still works.
   * @param {SlideshowSelectEvent} event
   */
  #handleSlideshowSelect = (event) => {
    this.#pauseAllMedia(event.detail?.slide);
  };

  /**
   * Pauses deferred media (videos / models) in this gallery.
   * @param {Element} [exceptSlide] - Skip media inside this slide (usually the active one).
   */
  #pauseAllMedia(exceptSlide) {
    this.querySelectorAll("deferred-media, product-model").forEach((media) => {
      if (!(media instanceof HTMLElement)) return;
      if (exceptSlide instanceof Element && exceptSlide.contains(media)) return;
      if ("pauseMedia" in media && typeof media.pauseMedia === "function") {
        media.pauseMedia();
      }
    });
  }

  /**
   * Filters media gallery items by type (gallery images or videos).
   *
   * @param {'gallery' | 'video'} type - The media type to show.
   * @param {Event} [event] - The click event from the filter button.
   */
  filterMediaType(type, event) {
    event?.preventDefault();

    const mediaType = type === "video" ? "video" : "gallery";

    this.#pauseAllMedia();
    this.dataset.mediaTypeFilter = mediaType;
    this.#syncFilterButtons(mediaType);
    this.#applyMediaTypeFilter(mediaType);
  }

  /**
   * Updates aria-selected state on filter buttons.
   *
   * @param {'gallery' | 'video'} mediaType
   */
  #syncFilterButtons(mediaType) {
    this.querySelectorAll("[data-media-filter]").forEach((button) => {
      if (!(button instanceof HTMLElement)) return;

      button.setAttribute("aria-selected", `${button.dataset.mediaFilter === mediaType}`);
    });
  }

  /**
   * Shows or hides slides, thumbnails, dots, and grid items by media type.
   *
   * @param {'gallery' | 'video'} mediaType
   */
  #applyMediaTypeFilter(mediaType) {
    const slideshow = this.slideshow;
    const slides = slideshow?.refs?.slides ?? [];
    const thumbnails = slideshow?.refs?.thumbnails ?? [];
    const dots = slideshow?.refs?.dots ?? [];
    const gridMedia = this.refs.media ?? [];

    let firstVisibleSlideId = null;

    slides.forEach((slide, index) => {
      const matches = slide.getAttribute("data-media-type") === mediaType;

      slide.toggleAttribute("hidden", !matches);
      slide.removeAttribute("reveal");

      if (!matches) {
        slide.setAttribute("aria-hidden", "true");
      }

      thumbnails[index]?.toggleAttribute("hidden", !matches);
      dots[index]?.toggleAttribute("hidden", !matches);

      if (matches && firstVisibleSlideId == null) {
        firstVisibleSlideId = slide.getAttribute("slide-id");
      }
    });

    gridMedia.forEach((item) => {
      if (!(item instanceof HTMLElement)) return;

      // Zoom dialog also uses ref="media[]"; only filter typed gallery items.
      if (!item.hasAttribute("data-media-type")) return;

      const matches = item.getAttribute("data-media-type") === mediaType;

      item.toggleAttribute("hidden", !matches);
    });

    if (slideshow && firstVisibleSlideId != null) {
      slideshow.select({ id: firstVisibleSlideId }, undefined, { animate: false });
    }
  }

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
